/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSystemMiscList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing System Misc list data and DataGrid state.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { fetchSystemMiscConfigs } from '../api';
import { useSystemMiscColumns } from './useSystemMiscColumns';
import { SystemMiscConfig } from '../types';

export function useSystemMiscList() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug ?? t('systemMisc.defaultTenant');
  const userId = user?.id;

  const availableColumns = useSystemMiscColumns();

  const fetchData = useMemo(
    () => async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      return fetchSystemMiscConfigs(params, tenantSlug, userId ? Number(userId) : undefined);
    },
    [tenantSlug, userId],
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'systemMisc',
  });

  const configs = useMemo(() => data as SystemMiscConfig[], [data]);

  return {
    configs,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  };
}

